import { CircularProgress, Grid, Paper, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { MinimalPageLayout } from '../components/Navigation/MinimalLayout/MinimalPageLayout';
import Loader from '../components/Shared/Loader';
import { StayInContactIcon } from '../components/Shared/StayInContactIcon';
import { gettingStartedRoute } from '../routes';
import { useGetOrderStatus } from '../sic-lib/api/hooks/useOrders';
import { useAppDispatch } from '../store/hooks';
import { requestConfig } from '../store/users';

export default function OrderSuccessPage() {
    const { classes } = useStyles();
    const history = useHistory();
    const dispatch = useAppDispatch();

    const { sessionId } = useParams<{ sessionId: string }>();
    const { data: orderStatusData, isLoading: isOrderStatusLoading } = useGetOrderStatus(sessionId);

    const orderStatus = useMemo(() => {
        switch (orderStatusData?.status) {
            case 'Success':
                return `Order successful! Please wait while we redirect you to the dashboard.`;
            case 'PaymentSuccess':
                return (
                    <div>
                        <Typography variant="h5" align="center">
                            Thank You for Your Purchase!
                        </Typography>
                        <Typography variant="h5" align="center">
                            Your payment has been successfully processed.
                        </Typography>
                        <Typography variant="h5" align="center">
                            We appreciate your business and look forward to assisting you further. A
                            member of our team will reach out to you within the next 24 business
                            hours.
                        </Typography>
                        <Typography variant="h5" align="center">
                            If you have any immediate questions or need assistance, please feel free
                            to contact us at Support@SuccessWebsite.com
                        </Typography>
                    </div>
                );
            case 'PaymentFailed':
                return 'Payment failed. Please try again.';
            case 'OrderActive':
                return `Please wait while we process your payment.`;
            default:
                return 'Unknown';
        }
    }, [orderStatusData]);

    useEffect(() => {
        if (orderStatusData?.status === 'Success') {
            dispatch(requestConfig());
            history.push(gettingStartedRoute);
        }
    }, [orderStatusData, history, dispatch]);

    return (
        <MinimalPageLayout>
            <StayInContactIcon className={classes.icon} />
            <Loader isLoading={isOrderStatusLoading}>
                <Paper>
                    <Grid
                        container
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        spacing={4}
                        p={4}
                    >
                        {orderStatusData?.status === 'OrderActive' && (
                            <Grid item display="flex" justifyContent="center" alignContent="center">
                                <CircularProgress />
                            </Grid>
                        )}
                        <Grid item>
                            <Typography variant="h5" align="center">
                                {orderStatus}
                            </Typography>
                        </Grid>
                        {orderStatusData?.status === 'PaymentFailed' && (
                            <Grid item>
                                <Typography variant="h6" align="center">
                                    {orderStatusData?.statusDescription}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Paper>
            </Loader>
        </MinimalPageLayout>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    nestedFlex: {
        width: '100%',
        textAlign: 'center',
    },
    linkText: {
        color: theme.palette.common.white,
    },
    icon: {
        maxWidth: '250px',
    },
}));
